import { React, useEffect, useRef, useState, useContext } from 'react'
import Grid from '@material-ui/core/Grid';
import './mediaPlayer.css'
import CircularProgress from '@material-ui/core/CircularProgress';
import CustomizedContentContext from '../contexts/CustomizedContentContext';
import AssignmentService from '../services/AssignmentService';
import { useHistory, useLocation } from 'react-router-dom';

const VDOCIPHER_V2_API_URL = 'https://player.vdocipher.com/v2/api.js';
const VDOCIPHER_V2_BASE_URL = 'https://player.vdocipher.com/v2/';
const PRIMARY_COLOR = 'FA2742';
const PLAY_ICON_RED = '#E63946';
const PLAY_ICON_INNER = '#8B2635';

function MediaPlayer() {

    const history = useHistory();
    const currentLoaction = useLocation();

    const {
        timerCount,
        customizedContent,
        playBackData,
        setCheckStatus
    } = useContext(CustomizedContentContext);

    const timeoutTimer = useRef();
    const iframeRef = useRef();
    const vdoPlayer = useRef();
    const eventListenersRef = useRef([]);
    const timerCountRef = useRef(timerCount);
    const customizedContentRef = useRef(customizedContent);
    const [playerStatus, setPlayerStatus] = useState(-1);
    const [hasUnlockedPlayback, setHasUnlockedPlayback] = useState(false);
    const [apiReady, setApiReady] = useState(typeof window !== 'undefined' && !!window.VdoPlayer);

    timerCountRef.current = timerCount;
    customizedContentRef.current = customizedContent;

    useEffect(() => {
        const listen = history.listen((location) => {
            if (currentLoaction !== location) {
                if (timeoutTimer.current !== undefined) {
                    clearInterval(timeoutTimer.current);
                    timeoutTimer.current = null;
                }
            }
        });

        return () => listen();

    }, [history, currentLoaction]);

    const secondsToTime = (e) => {

        if (e >= 0) {
            const h = Math.floor(e / 3600).toString().padStart(2, '0'),
                m = Math.floor(e % 3600 / 60).toString().padStart(2, '0'),
                s = Math.floor(e % 60).toString().padStart(2, '0');
            return h + ':' + m + ':' + s;
        }

        return '00:00:00';
    }

    useEffect(() => {
        if (typeof window === 'undefined' || window.VdoPlayer) {
            setApiReady(true);
            return;
        }
        const existing = document.querySelector(`script[src="${VDOCIPHER_V2_API_URL}"]`);
        if (existing) {
            if (window.VdoPlayer) {
                setApiReady(true);
            } else {
                window.onVdoPlayerV2APIReady = () => setApiReady(true);
            }
            return () => { delete window.onVdoPlayerV2APIReady; };
        }
        const script = document.createElement('script');
        script.src = VDOCIPHER_V2_API_URL;
        script.async = true;
        document.body.appendChild(script);
        window.onVdoPlayerV2APIReady = () => setApiReady(true);
        return () => { delete window.onVdoPlayerV2APIReady; };
    }, []);

    const buildIframeSrc = () => {
        const otp = playBackData?.otp;
        const playbackInfo = playBackData?.playbackInfo;
        if (!otp || playbackInfo == null) return null;
        const playbackInfoStr = typeof playbackInfo === 'string'
            ? playbackInfo
            : JSON.stringify(playbackInfo);
        const params = new URLSearchParams({
            otp: String(otp),
            playbackInfo: playbackInfoStr,
            loop: 'true',
            primaryColor: PRIMARY_COLOR
        });
        return `${VDOCIPHER_V2_BASE_URL}?${params.toString()}`;
    };

    const iframeSrc = buildIframeSrc();

    const initPlayer = useRef(() => {
        const iframe = iframeRef.current;
        if (!iframe || !window.VdoPlayer) return;

        const player = window.VdoPlayer.getInstance(iframe);
        if (!player) return;

        vdoPlayer.current = player;
        setCheckStatus(1);

        const onPlay = () => {
            setHasUnlockedPlayback(true);
            setPlayerStatus(1);
        };
        const onPause = () => setPlayerStatus(0);
        const onLoadStart = () => setPlayerStatus(-1);
        const onCanPlay = () => setPlayerStatus(0);

        player.video.addEventListener('play', onPlay);
        player.video.addEventListener('pause', onPause);
        player.video.addEventListener('loadstart', onLoadStart);
        player.video.addEventListener('canplay', onCanPlay);

        eventListenersRef.current = [
            () => player.video.removeEventListener('play', onPlay),
            () => player.video.removeEventListener('pause', onPause),
            () => player.video.removeEventListener('loadstart', onLoadStart),
            () => player.video.removeEventListener('canplay', onCanPlay)
        ];

        const runTimerOperations = async () => {
            const playerInstance = vdoPlayer.current;
            if (!playerInstance?.api) return;
            try {
                const totalPlayed = await playerInstance.api.getTotalPlayed();
                const count = timerCountRef.current;
                const content = customizedContentRef.current;
                const newTimerCount = count - totalPlayed;
                const timerCounter = document.getElementById('tp');

                if (timerCounter) {
                    timerCounter.innerHTML = secondsToTime(newTimerCount);
                }

                if (newTimerCount % 5 === 0 && newTimerCount !== -100 && content?.id != null) {
                    AssignmentService.updateCustomizedContent(content.id, newTimerCount)
                        .then(() => { })
                        .catch(() => { });
                }

                if (newTimerCount !== -100 && newTimerCount <= 0) {
                    await playerInstance.video.pause();
                    setCheckStatus(3);
                    window.location.reload();
                }
            } catch (_) {
                // ignore getTotalPlayed errors
            }
        };

        timeoutTimer.current = setInterval(runTimerOperations, 1000);
    });

    const handleIframeLoad = () => {
        if (window.VdoPlayer) {
            initPlayer.current();
        } else {
            window.onVdoPlayerV2APIReady = () => initPlayer.current();
        }
    };

    useEffect(() => {
        setHasUnlockedPlayback(false);
        return () => {
            if (timeoutTimer.current != null) {
                clearInterval(timeoutTimer.current);
                timeoutTimer.current = null;
            }
            eventListenersRef.current.forEach((remove) => remove());
            eventListenersRef.current = [];
            vdoPlayer.current = null;
        };
    }, [iframeSrc]);

    return (
        <>
            <Grid container direction="row" justifyContent="center" alignItems="center" style={{ backgroundColor: '#FFFF' }}>
                <Grid item xs={12} sm={12} md={12}>
                    <div className='mediaPlayerWrapper' style={{ position: 'relative' }}>
                        <div
                            className="container media-control-tappable"
                            role="button"
                            tabIndex={0}
                            aria-label="Play"
                            style={{
                                display: (playerStatus === 0 || playerStatus === 2 || playerStatus === 3) ? 'grid' : 'none',
                                pointerEvents: hasUnlockedPlayback ? 'auto' : 'none',
                                cursor: 'pointer'
                            }}
                            onClick={hasUnlockedPlayback ? () => vdoPlayer.current?.video?.play()?.catch(() => {}) : undefined}
                            onKeyDown={hasUnlockedPlayback ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); vdoPlayer.current?.video?.play()?.catch(() => {}); } } : undefined}
                        >
                            <div className="item">
                                <svg
                                    className="play-icon-custom"
                                    viewBox="0 0 100 100"
                                    aria-hidden="true"
                                >
                                    <circle cx="50" cy="50" r="45" fill={PLAY_ICON_INNER} stroke={PLAY_ICON_RED} strokeWidth="5" />
                                    <path fill={PLAY_ICON_RED} d="M40 28 L40 72 L68 50 Z" />
                                </svg>
                            </div>
                            <div className="circle-still" style={{ animationDelay: '0s' }}></div>
                        </div>
                        <div
                            className='container media-control-tappable'
                            role="button"
                            tabIndex={0}
                            aria-label="Pause"
                            style={{
                                display: playerStatus === 1 ? 'grid' : 'none',
                                pointerEvents: 'auto',
                                cursor: 'pointer'
                            }}
                            onClick={() => vdoPlayer.current?.video?.pause()?.catch(() => {})}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); vdoPlayer.current?.video?.pause()?.catch(() => {}); } }}
                        >
                            <div className="item">
                                <svg
                                    className="play-icon-custom"
                                    viewBox="0 0 100 100"
                                    aria-hidden="true"
                                >
                                    <circle cx="50" cy="50" r="45" fill={PLAY_ICON_INNER} stroke={PLAY_ICON_RED} strokeWidth="5" />
                                    <rect x="35" y="28" width="12" height="44" rx="2" fill={PLAY_ICON_RED} />
                                    <rect x="53" y="28" width="12" height="44" rx="2" fill={PLAY_ICON_RED} />
                                </svg>
                            </div>
                            <div className="circle" style={{ animationDelay: '0s' }}></div>
                            <div className="circle" style={{ animationDelay: '1s' }}></div>
                            <div className="circle" style={{ animationDelay: '2s' }}></div>
                        </div>
                        <div
                            className='container'
                            style={{
                                display: playerStatus === -1 ? 'grid' : 'none',
                                pointerEvents: 'none'
                            }}
                        >
                            <div className="item">
                                <CircularProgress style={{ color: 'white' }} size="large" sx={{ width: '10em', height: '10em' }} />
                            </div>
                            <div className="circle-still" style={{ animationDelay: '0s' }}></div>
                        </div>
                        <div className='container-sub'>
                            <div style={{ color: 'white' }}>
                                <div id="tp">{`${secondsToTime(timerCount)}`}</div>
                            </div>
                        </div>
                        <div
                            className="player"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 0,
                                minHeight: '200px'
                            }}
                        >
                            {apiReady && iframeSrc && (
                                <iframe
                                    key={iframeSrc}
                                    ref={iframeRef}
                                    src={iframeSrc}
                                    style={{ border: 0, width: '100%', height: '100%', minHeight: '200px' }}
                                    allow="encrypted-media"
                                    allowFullScreen
                                    title="VdoCipher player"
                                    onLoad={handleIframeLoad}
                                />
                            )}
                        </div>
                    </div>
                </Grid>
            </Grid>
        </>
    )
}

export default MediaPlayer
